import { useRef, useState } from "react";
import {
  AlertCircle,
  ArrowUpRight,
  CheckCircle2,
  Clock3,
  FileText,
  FileUp,
  History,
  Landmark,
  RefreshCw,
  ShieldCheck,
  Wallet,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import "./HistoricalSalesPage.css";

const money = (value) =>
  value == null || value === ""
    ? "—"
    : Number(value).toLocaleString("es-BO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const valueOf = (obj, ...keys) => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null && obj[key] !== "") {
      return obj[key];
    }
  }
  return null;
};

export default function HistoricalSalesPage() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmedAccount, setConfirmedAccount] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("resumen");

  const extracted = preview?.extracted_fields || preview?.extracted || {};
  const summary = preview?.summary || {};
  const rows = preview?.installment_rows || [];
  const paidCount = rows.filter(r => (r.display_status || r.source_status || "").toLowerCase() === "pagado").length;
  const overdueCount = rows.filter(r => (r.display_status || "").toLowerCase() === "vencido").length;
  const pendingCount = rows.filter(r => (r.display_status || r.source_status || "").toLowerCase() === "pendiente").length;

  const warnings = preview?.warnings || [];

  const updateExtracted = (key, value) => {
    setPreview(current => {
      if (!current) return current;
      const data = current.extracted_fields || current.extracted || {};
      const updated = { ...data, [key]: value };
      return { ...current, extracted: updated, extracted_fields: updated };
    });
  };


  const chooseFile = (event) => {
    const selected = event.target.files?.[0];
    setError("");
    setPreview(null);
    if (!selected) return;

    if (
      selected.type !== "application/pdf" &&
      !selected.name.toLowerCase().endsWith(".pdf")
    ) {
      setError("Selecciona un documento PDF.");
      event.target.value = "";
      setFile(null);
      return;
    }

    if (selected.size > 15 * 1024 * 1024) {
      setError("El archivo supera el límite de 15 MB aceptado por el servidor.");
      event.target.value = "";
      setFile(null);
      return;
    }

    setFile(selected);
  };

  const analyzePdf = async () => {
    if (!file || loading) return;
    setLoading(true);
    setError("");
    setPreview(null);

    try {
      const body = new FormData();
      body.append("file", file);

      const response = await api.post(
        "/cobranzas/historicas/importar-pdf/preview",
        body,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setPreview(response.data);
      setTab("resumen");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };


  const confirmImport = async (approved = false) => {
    if (!preview || saving || confirmedAccount) return;

    const data = preview.extracted_fields || preview.extracted || {};
    const initialRow = rows.find(
      row => Number(row.number ?? row.installment_number ?? 0) === 0
    );
    const num = (key, fallback = "") => {
      const raw = data[key] ?? fallback;
      if (raw === "" || raw == null) return null;
      const value = Number(String(raw).replace(/,/g, ""));
      return Number.isFinite(value) ? value : null;
    };

    const saleNumber = String(data.sale_number ?? data.original_sale_number ?? "").trim();
    const ownerName = String(data.owners ?? data.owner_name ?? "").trim();
    const originalPrice = num("original_price", data.sale_price);
    const discount = num("discount_amount", 0);
    const contractAmount = num("contract_amount");
    const downPayment = num("down_payment_amount", initialRow?.amount ?? "");
    const financedBalance = num("financed_balance");
    const area = num("area_m2");
    const currency = String(data.currency || "USD").trim().toUpperCase();

    if (!saleNumber || ownerName.length < 2) {
      setError("Completa el número de venta y el nombre del propietario.");
      return;
    }
    if (
      originalPrice === null || originalPrice <= 0 ||
      discount === null || discount < 0 ||
      contractAmount === null || contractAmount <= 0 ||
      downPayment === null || downPayment < 0 ||
      financedBalance === null || financedBalance < 0
    ) {
      setError("Completa y revisa precio original, descuento, importe contractual, inicial y saldo financiado.");
      return;
    }
    if (
      discount > originalPrice ||
      contractAmount > originalPrice + 0.01 ||
      downPayment > contractAmount + 0.01 ||
      financedBalance > contractAmount + 0.01
    ) {
      setError("Los importes financieros no son coherentes. Revisa los valores antes de guardar.");
      return;
    }
    if (!["USD", "BOB"].includes(currency)) {
      setError("La moneda debe ser USD o BOB.");
      return;
    }
    if (!preview.import_id) {
      setError("No se encontró el identificador de esta importación. Vuelve a analizar el PDF.");
      return;
    }
    if (!approved) {
      setShowConfirmModal(true);
      return;
    }
    setShowConfirmModal(false);

    const sale = {
      original_sale_number: saleNumber,
      owner_name: ownerName,
      owner_ci: String(data.owner_ci || ""),
      owner_phone: String(data.owner_phone || ""),
      owner_email: String(data.owner_email || ""),
      manzano: String(data.manzano || ""),
      lote: String(data.lote || ""),
      ...(area !== null && area > 0 ? { area_m2: area } : {}),
      currency,
      original_price: originalPrice,
      discount_amount: discount,
      contract_amount: contractAmount,
      down_payment_amount: downPayment,
      financed_balance: financedBalance,
      source_system: String(data.source_system || "Sistema anterior"),
      contract_reference: saleNumber,
      ...(data.sale_date ? { sale_date: data.sale_date } : {}),
      observations: "Importación histórica desde PDF. Los estados de las cuotas reflejan el documento de origen, no una verificación bancaria."
    };

    setSaving(true);
    setError("");
    try {
      const response = await api.post(
        "/cobranzas/historicas/importar-pdf/confirm",
        { import_id: preview.import_id, sale }
      );
      setConfirmedAccount(response.data?.account || response.data);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setConfirmedAccount(null);
    setError("");
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="hs-page">
      <header className="hs-header">
        <div>
          <div className="hs-eyebrow">
            <History size={15} /> CONTROL COMERCIAL · ARCHIVO HISTÓRICO
          </div>
          <h1>Ventas históricas</h1>
          <p className="hs-subtitle">
            Recupera contratos antiguos, revisa sus planes de pago y prepara
            la conciliación de cobranzas.
          </p>
        </div>
        <div className="hs-header-badge">
          <ShieldCheck size={17} /> Módulo independiente
        </div>
      </header>

      <section className="hs-stats">
        <article className="hs-stat-card">
          <span className="hs-stat-icon blue"><FileText size={20} /></span>
          <div>
            <span className="hs-stat-label">Documento analizado</span>
            <strong>{preview ? "1" : "0"}</strong>
            <small>{preview ? "Vista previa disponible" : "Aún sin analizar"}</small>
          </div>
        </article>

        <article className="hs-stat-card">
          <span className="hs-stat-icon amber"><Clock3 size={20} /></span>
          <div>
            <span className="hs-stat-label">Cuotas detectadas</span>
            <strong>{preview ? (summary.total_rows ?? rows.length) : "—"}</strong>
            <small>Requieren revisión</small>
          </div>
        </article>

        <article className="hs-stat-card">
          <span className="hs-stat-icon green"><Wallet size={20} /></span>
          <div>
            <span className="hs-stat-label">Importe del documento</span>
            <strong>
              {preview
                ? money(valueOf(extracted, "sale_price", "contract_amount", "total_amount"))
                : "—"}
            </strong>
            <small>
              {preview ? "Importe extraído · no conciliado" : "Se mostrará al analizar"}
            </small>
          </div>
        </article>

        <article className="hs-stat-card">
          <span className="hs-stat-icon violet"><Landmark size={20} /></span>
          <div>
            <span className="hs-stat-label">Estado contable</span>
            <strong className="hs-stat-status">{preview ? `${paidCount} pagadas · ${pendingCount} pendientes · ${overdueCount} vencidas` : "Sin datos"}</strong>
            <small>Pagos históricos conservados según el PDF</small>
          </div>
        </article>
      </section>

      <section className="hs-main-grid">
        <article className="hs-card hs-upload-card">
          <div className="hs-card-heading">
            <div>
              <span className="hs-step">01</span>
              <div>
                <h2>Importar contrato</h2>
                <p>Analiza un PDF sin registrar ventas ni pagos.</p>
              </div>
            </div>
            <FileUp size={21} />
          </div>

          <button
            type="button"
            className={`hs-dropzone ${file ? "has-file" : ""}`}
            onClick={() => inputRef.current?.click()}
          >
            <span className="hs-upload-icon"><FileUp size={27} /></span>
            <strong>{file ? file.name : "Selecciona un contrato PDF"}</strong>
            <span>
              {file
                ? `${(file.size / 1024 / 1024).toFixed(2)} MB · Listo para analizar`
                : "Haz clic para buscar el archivo en tu equipo"}
            </span>
            <small>PDF · Máximo 25 MB</small>
          </button>

          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={chooseFile}
            hidden
          />

          {error && (
            <div className="hs-error">
              <AlertCircle size={17} />{error}
            </div>
          )}

          <div className="hs-upload-actions">
            <button
              type="button"
              className="hs-primary-btn"
              disabled={!file || loading}
              onClick={analyzePdf}
            >
              {loading ? (
                <><RefreshCw className="hs-spin" size={17} /> Analizando documento…</>
              ) : (
                <><FileText size={17} /> Generar vista previa</>
              )}
            </button>
            <button type="button" className="hs-secondary-btn" onClick={reset}>
              Limpiar
            </button>
          </div>

          <div className="hs-security-note">
            <ShieldCheck size={17} />
            <span>
              <strong>Importación segura</strong>
              La vista previa no crea ventas ni registra pagos en la contabilidad.
            </span>
          </div>
        </article>

        <article className="hs-card hs-process-card">
          <div className="hs-card-heading">
            <div>
              <span className="hs-step">02</span>
              <div>
                <h2>Proceso de revisión</h2>
                <p>El documento pasa por controles antes de cualquier registro.</p>
              </div>
            </div>
          </div>

          <div className="hs-process-list">
            <div className="hs-process-item active">
              <span><FileText size={17} /></span>
              <div><strong>Extracción del contrato</strong><small>Lectura de datos y calendario de cuotas</small></div>
              <CheckCircle2 size={17} />
            </div>
            <div className={`hs-process-item ${preview ? "active" : ""}`}>
              <span><AlertCircle size={17} /></span>
              <div><strong>Revisión de información</strong><small>Validar lote, propietarios, importes y vencimientos</small></div>
              <Clock3 size={17} />
            </div>
            <div className="hs-process-item">
              <span><Landmark size={17} /></span>
              <div><strong>Conciliación contable</strong><small>Contrastar recibos y pagos registrados antes de confirmar</small></div>
              <Clock3 size={17} />
            </div>
          </div>
        </article>
      </section>

      {preview && (
        <section className="hs-card hs-results">
          <div className="hs-results-head">
            <div>
              <h2>Resultado del análisis</h2>
              <p>Datos extraídos del PDF. Requieren revisión antes de cualquier registro.</p>
            </div>
            <span className="hs-source-status">Vista previa · Sin confirmar</span>
          </div>

          <div className="hs-contract-grid hs-editable-grid">
            {[
              ["N.º de venta / contrato", "sale_number", valueOf(extracted, "sale_number", "original_sale_number", "contract_number", "numero_venta"), "text"],
              ["Propietario(s)", "owners", valueOf(extracted, "owners", "owner", "buyer", "client_name"), "text"],
              ["Celular del cliente", "owner_phone", valueOf(extracted, "owner_phone", "phone", "mobile", "telefono", "celular") || "", "tel"],
              ["Manzano", "manzano", valueOf(extracted, "manzano", "block", "block_number"), "text"],
              ["Lote", "lote", valueOf(extracted, "lote", "lot", "lot_number"), "text"],
              ["Superficie (m²)", "area_m2", valueOf(extracted, "area_m2", "area", "surface"), "number"],
              ["Fecha de venta", "sale_date", valueOf(extracted, "sale_date"), "date"],
              ["Moneda (USD o BOB)", "currency", valueOf(extracted, "currency") || "USD", "text"],
              ["Precio original", "original_price", valueOf(extracted, "original_price", "sale_price"), "number"],
              ["Descuento", "discount_amount", valueOf(extracted, "discount_amount") ?? "0", "number"],
              ["Importe contractual", "contract_amount", valueOf(extracted, "contract_amount"), "number"],
              ["Inicial contractual", "down_payment_amount", valueOf(extracted, "down_payment_amount") ?? rows.find(row => Number(row.number ?? row.installment_number ?? 0) === 0)?.amount ?? "", "number"],
              ["Saldo financiado", "financed_balance", valueOf(extracted, "financed_balance"), "number"],
              ["Sistema de origen", "source_system", valueOf(extracted, "source_system") || "Sistema anterior", "text"],
            ].map(([label, key, value, type]) => (
              <label className="hs-editable-field" key={key}>
                <span>{label}</span>
                <input
                  type={type}
                  min={type === "number" ? "0" : undefined}
                  step={type === "number" ? "0.01" : undefined}
                  value={value == null ? "" : String(value)}
                  onChange={event => updateExtracted(key, event.target.value)}
                  placeholder="Revisar o completar"
                  aria-label={label}
                />
              </label>
            ))}
          </div>

          <div className="hs-tabs">
            <button type="button" className={tab === "resumen" ? "selected" : ""} onClick={() => setTab("resumen")}>Resumen</button>
            <button type="button" className={tab === "cuotas" ? "selected" : ""} onClick={() => setTab("cuotas")}>Cuotas ({rows.length})</button>
            <button type="button" className={tab === "texto" ? "selected" : ""} onClick={() => setTab("texto")}>Texto extraído</button>
          </div>

          {tab === "resumen" && (
            <div className="hs-summary-content">
              <div className="hs-summary-cards">
                <div><span>Filas detectadas</span><strong>{summary.total_rows ?? rows.length}</strong></div>
                <div><span>Cuota inicial</span><strong>{summary.initial_rows ?? "—"}</strong></div>
                <div><span>Cuotas numeradas</span><strong>{summary.numbered_installments ?? "—"}</strong></div>
                <div><span>Pagadas · histórico</span><strong className="hs-status-paid">{paidCount}</strong></div><div><span>Pendientes</span><strong className="hs-status-pending">{pendingCount}</strong></div><div><span>Vencidas</span><strong className="hs-status-overdue">{overdueCount}</strong></div>
              </div>
              {warnings.length > 0 && (
                <div className="hs-warning-list">
                  <h3><AlertCircle size={16} /> Observaciones que requieren revisión</h3>
                  {warnings.map((warning, index) => (
                    <p key={index}><AlertCircle size={14} />{typeof warning === "string" ? warning : warning.message || JSON.stringify(warning)}</p>
                  ))}
                </div>
              )}
              <div className="hs-final-notice">
                <ShieldCheck size={19} />
                <div><strong>Estado histórico del documento</strong><p>“Pagado” significa que así aparece en el PDF de origen. No representa conciliación bancaria ni verificación contable. Esta vista previa no registra ventas ni pagos.</p></div>
              </div>
            </div>
          )}

          {tab === "cuotas" && (
            <div className="hs-table-wrap">
              <table className="hs-table">
                <thead><tr><th>N.º</th><th>Vencimiento</th><th>Importe</th><th>Saldo impreso</th><th>Estado en PDF</th><th>Estado contable</th></tr></thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan="6" className="hs-empty">El backend no devolvió filas de cuotas.</td></tr>
                  ) : rows.map((row, index) => (
                    <tr key={row.id || row.installment_number || index}>
                      <td>{row.installment_number ?? row.number ?? row.numero ?? index + 1}</td>
                      <td>{row.due_date ?? row.due_date_text ?? row.fecha_vencimiento ?? "—"}</td>
                      <td>{money(valueOf(row, "amount", "installment_amount", "importe", "cuota"))}</td>
                      <td>{money(valueOf(row, "printed_balance", "balance", "saldo", "saldo_impreso"))}</td>
                      <td><span className="hs-source-status">{row.source_status ?? row.pdf_status ?? row.status_in_pdf ?? row.estado_impreso ?? "No indicado"}</span></td>
                      <td>{(() => {
                        const status = String(row.display_status || row.source_status || "pendiente").toLowerCase();
                        const label = status === "pagado" ? "Pagado" : status === "vencido" ? "Vencido" : "Pendiente";
                        return <span className={`hs-pending-pill hs-status-${label.toLowerCase()}`}>{label}</span>;
                      })()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "texto" && (
            <pre className="hs-text-preview">{preview.text_preview || "El backend no devolvió texto de vista previa."}</pre>
          )}

          {confirmedAccount ? (
            <div className="hs-final-notice">
              <CheckCircle2 size={19} />
              <div>
                <strong>Venta histórica guardada</strong>
                <p>Registro: {confirmedAccount.original_sale_number || confirmedAccount.id}. El estado histórico se mantiene separado de la verificación contable.</p>
              </div>
            </div>
          ) : (
            <div className="hs-confirm-actions">
              <button className="hs-confirm-primary" type="button" onClick={confirmImport} disabled={saving || loading}>
                {saving ? "Guardando contrato…" : "Confirmar e importar contrato"}
              </button>
              <p>Revisa los datos financieros antes de confirmar.</p>
            </div>
          )}
        </section>
      )}
          {showConfirmModal && (
        <div className="hs-modal-overlay" role="presentation" onMouseDown={(e) => {
          if (e.target === e.currentTarget) setShowConfirmModal(false);
        }}>
          <section className="hs-modal" role="dialog" aria-modal="true" aria-labelledby="hs-modal-title">
            <div className="hs-modal-icon">!</div>
            <h2 id="hs-modal-title">Confirmar importación histórica</h2>
            <p className="hs-modal-intro">
              Revisa los datos antes de guardar esta venta en la cartera.
            </p>
            <div className="hs-modal-summary">
              <div><span>Propietario</span><strong>{confirmedAccount?.owner_name || "No especificado"}</strong></div>
              <div><span>Número de venta</span><strong>{confirmedAccount?.original_sale_number || "No especificado"}</strong></div>
              <div><span>Precio de venta</span><strong>{confirmedAccount?.currency || "USD"} {Number(confirmedAccount?.contract_amount || confirmedAccount?.original_price || 0).toLocaleString("es-BO", {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong></div>
            </div>
            <div className="hs-modal-warning">
              <strong>Importante: conciliación pendiente</strong>
              <p>Los pagos que figuran como «Pagado» en el PDF son antecedentes del documento. No se consideran conciliados con el banco hasta que sean verificados.</p>
            </div>
            <div className="hs-confirm-actions">
              <button type="button" className="hs-confirm-secondary" onClick={() => setShowConfirmModal(false)}>
                Volver a revisar
              </button>
              <button type="button" className="hs-confirm-primary" onClick={() => confirmImport(true)} disabled={saving}>
                {saving ? "Guardando..." : "Confirmar importación"}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>



  );
}

